import api from 'Api/ApiMethods';
import i18next, { t } from 'i18next';
import { makeAutoObservable } from 'mobx';
import { getAxiosErrorMessage } from 'utils/httpErrorsHelpers';
import { Campaign } from 'models/restTypes';
import { RootStore } from './rootStore';

export class CampaignsStore {
  rootStore: RootStore = {} as RootStore;
  campaigns: Campaign[] = [];
  // notificationTemplates: NotificationTemplate[] = [];
  selectedCampaign: Campaign | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  setCampaigns = (campaigns: Campaign[]) => {
    this.campaigns = campaigns;
  };

  getAllCampaigns = async () => {
    try {
      const response = await api.getCampaigns();
      this.setCampaigns(response);
    } catch (error) {
      console.log('Error fetching campaigns', error);
      this.errorToast(this.getErrorMessage(error));
    }
  };

  resetCampaigns = () => {
    this.campaigns = [];
  };

  setSelectedCampaign = (campaign: Campaign) => {
    this.selectedCampaign = campaign;
  };

  private successToast(message: string): void {
    this.rootStore?.snackBarStore.showToast({
      detail: message,
      severity: 'success',
      summary: 'Success',
    });
  }

  createCampaign = async (campaign: Campaign) => {
    try {
      const response = await api.postCampaign(campaign);
      this.setCampaigns([response, ...this.campaigns]);
      this.selectedCampaign = response;
      this.successToast(t('campaigns:campaign_created_successfully'));
      return true;
    } catch (error) {
      console.log('Error creating campaign', error);
      this.errorToast(this.getErrorMessage(error));
      return false;
    }
  }

  updateCampaign = async (campaign: Campaign) => {
    try {
      const response = await api.updateCampaign(campaign);
      const newCampaigns = (this.campaigns || []).map((c) =>
        c.uuid === response.uuid ? response : c,
      );

      this.setCampaigns(newCampaigns);

      this.selectedCampaign = response;
      this.successToast(t('campaigns:campaign_updated_successfully'));
      return true;
    } catch (error) {
      console.log('Error updating campaign', error);
      this.errorToast(this.getErrorMessage(error));
      return false;
    }
  }

  deleteSelectedCampaign = async () => {
    console.log('try delete', this.selectedCampaign);
    if (!this.selectedCampaign) {
      return false;
    }

    try {
      await api.deleteCampaign(this.selectedCampaign.uuid);
      this.setCampaigns(
        (this.campaigns || []).filter(
          (c) => c.uuid !== this.selectedCampaign?.uuid,
        ),
      );

      this.selectedCampaign = null;
      this.successToast(t('campaigns:campaign_deleted_successfully'));
      return true;
    } catch (error) {
      console.log('Error deleting campaign', error);
      this.errorToast(this.getErrorMessage(error));
      return false;
    }
  };

  errorToast = (message: string) => {
    this.rootStore?.snackBarStore.showToast({
      detail: message,
    });
  };

  private getErrorMessage(error: unknown): string {
    return (
      getAxiosErrorMessage(error) ?? i18next.t('errors:something_went_wrong')
    );
  }
}
